/**
 * Median, Mode, Range, IQR generator (6.DPSR.1.4).
 *
 * A whole-number data set is generated first, then a single measure of center
 * or spread is computed from it with the exact statistics engine. The measure
 * is validated to be a whole number before the question is emitted (otherwise
 * the attempt is retried), so a 6th-grade student always gets a clean integer
 * answer and the displayed data always yields the graded value.
 *
 * Families:
 *   F1: median (middle value)   (number)
 *   F2: mode (most frequent)     (number)
 *   F3: range (max - min)        (number)
 *   F4: IQR (Q3 - Q1)            (number)
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import {
  sortAscending,
  median,
  mode,
  range,
  fiveNumberSummary,
  iqr,
} from "../engine/statistics";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "measures-center-spread" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3" | "F4";

interface Cfg {
  readonly families: readonly Family[];
  /** Allowed data-set sizes (odd so median and quartiles land cleanly). */
  readonly sizes: readonly number[];
  readonly valueMax: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2", "F3"], sizes: [5, 7], valueMax: 20 },
  standard: { families: ["F1", "F2", "F3", "F4"], sizes: [7, 9], valueMax: 30 },
  hard: { families: ["F1", "F2", "F3", "F4"], sizes: [7, 9, 11], valueMax: 50 },
  challenge: { families: ["F2", "F3", "F4"], sizes: [9, 11], valueMax: 80 },
};

/** Build a whole-number data set of the given size. */
function makeData(rng: Rng, size: number, valueMax: number): Rational[] {
  const values: Rational[] = [];
  for (let i = 0; i < size; i++) values.push(rat(rng.int(1, valueMax)));
  return values;
}

export class MeasuresCenterSpreadGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);
      const size = rng.pick(cfg.sizes);

      const data = makeData(rng, size, cfg.valueMax);
      const sorted = sortAscending(data);

      let answer: Rational;
      let measureName: string;
      let hint1: string;
      let hint2: string;

      switch (family) {
        case "F1": {
          answer = median(sorted);
          measureName = "median";
          hint1 = "The median is the middle value once the data is in order.";
          hint2 = "Sort the values, then find the one in the center.";
          break;
        }
        case "F2": {
          const modes = mode(data);
          // Require exactly one mode so the answer is unique and whole.
          if (modes.length !== 1) continue;
          answer = modes[0] as Rational;
          measureName = "mode";
          hint1 = "The mode is the value that appears most often.";
          hint2 = "Tally how many times each value occurs.";
          break;
        }
        case "F3": {
          answer = range(sorted);
          measureName = "range";
          hint1 = "The range is the largest value minus the smallest value.";
          hint2 = "Subtract the minimum from the maximum.";
          break;
        }
        case "F4": {
          const summary = fiveNumberSummary(sorted);
          answer = iqr(summary);
          measureName = "interquartile range (IQR)";
          hint1 = "The IQR is the upper quartile minus the lower quartile.";
          hint2 = "Find Q1 and Q3 (excluding the median), then subtract Q1 from Q3.";
          break;
        }
      }

      // Clean-answer gate: only whole-number answers reach a 6th grader.
      if (!answer.isInteger()) continue;

      const valueList = sorted.map((v) => v.toString()).join(", ");

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: {
          text: `For the data set ${valueList}, find the ${measureName}.`,
        },
        answerType: "number",
        answer: { kind: "number", value: answer },
        steps: [
          {
            description: `Order the data and compute the ${measureName}.`,
            latex: answer.toLatex(),
          },
        ],
        hints: [
          { level: 1, text: hint1 },
          { level: 2, text: hint2 },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const measuresCenterSpread = new MeasuresCenterSpreadGenerator();
